import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import SearchComponent from '@components/inputs/search/Search.component';
import SelectComponent from '@components/inputs/select/Select.component';
import NewTableComponent from '@components/table/NewTable.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import {Add, Alert, Comment, Download, GroupAction} from '@icons/index';
import FolderIcon from '@mui/icons-material/Folder';
import {Box, Stack} from '@mui/material';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {useEffectFn} from '@ngneat/effects-hooks';
import {emitOnce} from '@ngneat/elf';
import {useObservable} from '@ngneat/react-rxjs';
import ExecutionGroupedActionModal from '@screens/admin/operations/executions/ExecutionGroupedAction.modal';
import {FilterBaseEntityEnum} from '@store/filters';
import {
  Execution,
  ExecutionFilterDate,
  ExecutionFilterSchedule,
  ExecutionFilterSource,
  executionsQuery,
  executionsService,
  ExecutionStatus,
  searchExecutionEffect,
} from '@store/operations/executions';
import {ServiceKindTypeEnum} from '@store/settings/serviceKinds';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {finalize} from 'rxjs';
import ExecutionReceiptModal from '@screens/admin/operations/executions/ExecutionReceipt.modal';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';
import {DoneAll} from '@mui/icons-material';

const ExecutionsTab = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const searchExecutions = useEffectFn(searchExecutionEffect);

  const [openGroupedAction, setOpenGroupedAction] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);

  const [CSVLoading, setCSVLoading] = useState(false);

  const [{executions, loading}] = useObservable(executionsQuery.executions$);
  const [selectedExecutions] = useObservable(executionsQuery.selectedExecutions$);

  const [filters] = useObservable(executionsQuery.filters$);
  const [sortModel] = useObservable(executionsQuery.sort$);
  const [tablePagination] = useObservable(executionsQuery.pagination$);

  const [totalExecutions, setTotalExecutions] = useState<number>(42);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    const customFilters = {
      from: filters.from,
      to: filters.to,
      search: filters.search,
      tableFilters: filters.tableFilters,
    };
    executionsService.countExecutionsWithParams(customFilters, sortModel, tablePagination).subscribe((data) => {
      setIsLoading(false);
      setTotalExecutions(data.count);
    });
    searchExecutions({filters, tablePagination, sort: sortModel});
  }, [filters, searchExecutions, sortModel, tablePagination]);

  const handleReceiptModal = () => {
    setIsLoading(true);
    const customFilters = {
      from: filters.from,
      to: filters.to,
      search: filters.search,
      tableFilters: filters.tableFilters,
      filterDate: filters.filterDate,
    };
    executionsService.countExecutionsWithParams(customFilters, sortModel, tablePagination).subscribe((data) => {
      setIsLoading(false);
      setTotalExecutions(data.count);
    });
    searchExecutions({filters, tablePagination, sort: sortModel});
    setOpenReceipt(false);
  };

  const handleExportCSV = () => {
    setCSVLoading(true);
    executionsService
      .getExecutionsCSV()
      .pipe(finalize(() => setCSVLoading(false)))
      .subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <Stack flex={1} width="100%" spacing={2} pb="25px" mt="-35px">
      <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
        <ExpandableIconButtonComponent
          variant="outlined"
          color="primary"
          disabled={!selectedExecutions.length}
          onClick={() => setOpenGroupedAction(true)}
          startIcon={<GroupAction/>}
          title={t('global.groupedAction')}
        />
        <ExpandableIconButtonComponent
          disabled={isLoading || !totalExecutions}
          variant="outlined"
          color="primary"
          onClick={() => setOpenReceipt(true)}
          startIcon={<DoneAll/>}
          title={t('global.receipt', {value: executionsQuery.totalRows})}
        />
        <ExpandableIconButtonComponent
          variant="outlined"
          color="primary"
          disabled={CSVLoading}
          onClick={handleExportCSV}
          startIcon={<Download/>}
          title={t('global.exportCSV')}
        />
        <ExpandableIconButtonComponent variant="contained" color="primary" onClick={() => navigate('add-batch')}
                                       startIcon={<Add/>} title={t('operations.addButtons.batchExecutions')}/>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <SelectComponent
            size="small"
            inputWidth={150}
            items={ExecutionFilterDate.selectItems()}
            value={filters.filterDate}
            handleChange={executionsService.setFilters('filterDate')}
            placeholder={t('executions.filters.date')}
          />
        </Box>
        <Box>
          <DateRangePickerComponent
            size="small"
            inputWidth={140}
            placeholderStart={t('invoices.executions.filters.from')}
            placeholderEnd={t('invoices.executions.filters.to')}
            startDate={filters.from}
            endDate={filters.to}
            handleChangeStartDate={executionsService.setFilters('from')}
            handleChangeEndDate={executionsService.setFilters('to')}
          />
        </Box>
        <Box>
          <SelectComponent
            size="small"
            inputWidth={100}
            items={ExecutionStatus.selectItems()}
            value={filters.status}
            handleChange={executionsService.setFilters('status')}
            placeholder={t('invoices.executions.filters.status')}
          />
        </Box>
        <Box>
          <SelectComponent
            size="small"
            inputWidth={100}
            items={ExecutionFilterSource.selectItems()}
            value={filters.source}
            handleChange={executionsService.setFilters('source')}
            placeholder={t('invoices.executions.filters.source')}
          />
        </Box>
        <Box>
          <SelectComponent
            size="small"
            inputWidth={100}
            items={ExecutionFilterSchedule.selectItems()}
            value={filters.schedule}
            handleChange={executionsService.setFilters('schedule')}
            placeholder={t('invoices.executions.filters.schedule')}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                executionsService.setFilters('search')(value);
                executionsService.setPagination('page')(1);
              });
            }}
          />
        </Box>
        <TableFiltersComponent
          baseEntity={FilterBaseEntityEnum.EXECUTION}
          filters={filters}
          handleSave={executionsService.setFilters('tableFilters')}
          handleReset={() => executionsService.resetFilters()}
        />
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.ICON({
            field: 'execution.isExecutionGroup',
            color: 'secondary',
            label: t('operations.executions.table.isExecutionGroup'),
            renderIcon: (execution: Execution) => {
              if (execution.isExecutionGroup === true) return <FolderIcon fontSize="small"/>;
            },
            sortable: true,
            width: 20,
          }),
          COLUMN_TYPES.TEXT({
            field: 'execution.createdAt',
            color: 'secondary',
            label: t('operations.executions.table.createdAt'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.createdAt,
            valueFormatter: (params) => (params.value ? t('global.dateAndHour', {date: new Date(params.value)}) : ''),
            sortable: true,
            width: 140,
          }),
          COLUMN_TYPES.TEXT({
            field: 'execution.plannedFrom',
            color: 'secondary',
            label: t('operations.executions.table.plannedFrom'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.plannedFrom,
            valueFormatter: (params) => (params.value ? t('global.date', {date: new Date(params.value)}) : ''),
            sortable: true,
            width: 110,
          }),
          COLUMN_TYPES.TEXT({
            field: 'execution.executedAt',
            color: 'secondary',
            label: t('operations.executions.table.executedAt'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.executedAt,
            valueFormatter: (params) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
            sortable: true,
            width: 110,
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'execution.generatedByScheduleID',
            label: t('operations.executions.table.creationType'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) =>
              params.row.kindType === ServiceKindTypeEnum.PROCESSING
                ? '-'
                : params.row.recurring
                  ? t('global.frequencies.recurrent')
                  : t('global.frequencies.onDemand'),
            sortable: true,
            width: 110,
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'serviceKind.kind',
            label: t('operations.executions.table.type'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.type,
            sortable: true,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'waste.name',
            color: 'secondary',
            label: t('operations.executions.table.waste'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.waste,
            sortable: true,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'container.name',
            color: 'secondary',
            label: t('operations.executions.table.container'),
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.container,
            sortable: true,
            width: 200,
          }),
          COLUMN_TYPES.TEXT({
            field: 'organisationUnit.name',
            color: 'secondary',
            label: t('operations.executions.table.client'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.clientOrganisationUnit?.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'supOrgUnit.name',
            color: 'secondary',
            label: t('operations.executions.table.supplier'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.supplierOrganisationUnit?.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.NUMBER({
            field: 'execution.quantity1',
            label: t('operations.executions.table.quantity'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.quantity1,
            width: 100,
          }),
          COLUMN_TYPES.TEXT({
            field: 'serviceKind.execUnitName1',
            color: 'secondary',
            label: t('operations.executions.table.unit'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.unit1,
            width: 100,
          }),
          COLUMN_TYPES.SELECT({
            field: 'execution.status',
            label: t('global.status'),
            options: ExecutionStatus.selectItems(),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.status,
            width: 150,
          }),
          COLUMN_TYPES.ICONS({
            field: 'properties',
            label: '',
            icons: [
              {
                icon: <Comment style={{width: 18, marginTop: 5, height: 21}}/>,
                hidden: (execution: Execution) => (execution.commentsCount ?? 0) <= 0,
              },
              {
                icon: <Alert style={{height: 18, width: 21}}/>,
                hidden: (execution: Execution) => (execution.issueCount ?? 0) <= 0,
              },
            ],
            width: 100,
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (execution: Execution) => navigate(`${execution.id}`),
            otherActions: [{type: OtherActionType.EDIT}],
          }),
        ]}
        handlePageSizeChange={executionsService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => executionsService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={executionsService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={executionsService.setSelectedExecutions}
        rowsSelected={selectedExecutions}
        totalRows={executionsQuery.totalRows}
        handleRowChanged={(execution: Execution) => {
          const updatedExecution = {
            // quantity1: execution["execution.quantity1"],
            status: execution['execution.status'],
          };
          executionsService.updateExecutionInList(execution.id, updatedExecution).subscribe({
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
          return {...execution, ...updatedExecution};
        }}
        data={executions}
      />
      <Outlet/>
      {openGroupedAction && <ExecutionGroupedActionModal handleClose={() => setOpenGroupedAction(false)}/>}
      {openReceipt && <ExecutionReceiptModal filters={filters} sortModel={sortModel} tablePagination={tablePagination}
                                             onSave={() => handleReceiptModal()}
                                             handleClose={() => setOpenReceipt(false)}/>}
    </Stack>
  );
};

export default ExecutionsTab;
